import { useCallback, useState } from "react";
import { doc, runTransaction } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";

export function useRemoveTenantAdmin(tenantId: string, tenantName: string) {
  const { t } = useTranslation(["admin"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const removeAdmin = useCallback(
    async (uid: string, email: string): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const tenantRef = doc(db, "tenants", tenantId);

        await runTransaction(db, async (tx) => {
          const snap = await tx.get(tenantRef);
          if (!snap.exists()) {
            throw new Error(`Tenant ${tenantId} does not exist`);
          }

          const data = snap.data();
          const currentAdminUids: string[] = data.admin_uids ?? [];
          const updatedAdminUids = currentAdminUids.filter((u) => u !== uid);

          if (updatedAdminUids.length === currentAdminUids.length) {
            return;
          }

          tx.update(tenantRef, {
            admin_uids: updatedAdminUids,
          });
        });

        toast.success(
          t("admin:tenants.detail.removeAdminDialog.success", {
            email,
            tenantName,
          }),
        );
        return true;
      } catch (err) {
        logError(err as Error);
        toast.error(
          t("admin:tenants.detail.removeAdminDialog.errorWriteFailed", {
            tenantName,
          }),
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tenantId, tenantName, t],
  );

  return { removeAdmin, isSubmitting };
}
